import { Component, OnInit, signal, computed, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PlaygroundSliderComponent } from "./playground-slider/playground-slider.component";
import { PlaygroundInitialComponent } from "./playground-initial/playground-initial.component";
import { PlaygroundProcessComponent } from "./playground-process/playground-process.component";
import { PlaygroundInQueuedComponent } from "./playground-in-queued/playground-in-queued.component";
import { PlaygroundContainerComponent } from "./playground-container/playground-container.component";
import { LoadingComponent } from "../../../components/loading/loading.component";
import { TaxonomyApiService } from '../../../services/api-services/taxonomy-api.service';

@Component({
  selector: 'app-taxonomy-playground-v2',
  standalone: true,
  imports: [
    CommonModule,
    PlaygroundSliderComponent,
    PlaygroundInitialComponent,
    PlaygroundProcessComponent,
    PlaygroundInQueuedComponent,
    PlaygroundContainerComponent,
    LoadingComponent
  ],
  templateUrl: './taxonomy-playground-v2.component.html',
  styleUrl: './taxonomy-playground-v2.component.scss'
})
export class TaxonomyPlaygroundV2Component implements OnInit {
  @ViewChild(PlaygroundSliderComponent) playgroundSlider!: PlaygroundSliderComponent;
  @ViewChild(PlaygroundContainerComponent) playgroundContainer!: PlaygroundContainerComponent;

  selectedTaxonomyId = signal<string | null>(null);
  selectedTest = signal<{taxonomyId: string, testId: string} | null>(null);
  testResultData = signal<any>(null);
  isLoadingTestResults = signal<boolean>(false);

  // Inject services
  private taxonomyApiService = inject(TaxonomyApiService);

  // Computed signal to determine which component to show
  currentView = computed(() => {
    const test = this.selectedTest();
    if (!test) return 'initial';

    if (test.testId === 'no-tests') return 'initial';

    // Get test status from the slider component's data
    const testStatus = this.getTestStatus(test.taxonomyId, test.testId);

    switch (testStatus) {
      case 'in_progress':
        return 'process';
      case 'queued':
        return 'in-queued';
      case 'processed':
        return 'container';
      default:
        return 'initial';
    }
  });

  ngOnInit() {
  }

  onTaxonomySelected(taxonomyId: string | null) {
    this.selectedTaxonomyId.set(taxonomyId);
    if (!taxonomyId) {
      this.selectedTest.set(null);
      this.testResultData.set(null);
    }
  }

  onTestSelected(testInfo: {taxonomyId: string, testId: string} | null) {
    this.selectedTest.set(testInfo);

    // If a processed test is selected, fetch the test results
    if (testInfo && testInfo.testId !== 'no-tests') {
      const testStatus = this.getTestStatus(testInfo.taxonomyId, testInfo.testId);
      if (testStatus === 'processed') {
        this.fetchTestResults(testInfo.testId);
      }
    } else {
      this.testResultData.set(null);
    }
  }

  // Handle when a new test is created
  onTestCreated(testInfo: { taxonomyId: string; testId: string }) {
    // Automatically select the newly created test
    this.selectedTest.set(testInfo);

    // Set loading state to true to show loader and prevent table flash
    this.isLoadingTestResults.set(true);

  }

  // Fetch test results from API
  private fetchTestResults(batchId: string) {
    // Set loading state
    this.isLoadingTestResults.set(true);

    this.taxonomyApiService.getTestResult({ batch_id: batchId }).subscribe({
      next: (response: any) => {
        if (response?.success && response?.data) {
          this.testResultData.set(response.data);

          // Add a delay to ensure the table is fully rendered before hiding the loader
          setTimeout(() => {
            this.isLoadingTestResults.set(false);
          }, 500); // 500ms delay to ensure table rendering is complete
        } else {
          console.error('Failed to fetch test results:', response);
          // Clear loading state on error
          this.isLoadingTestResults.set(false);
        }
      },
      error: (error: any) => {
        console.error('Error fetching test results:', error);
        // Clear loading state on error
        this.isLoadingTestResults.set(false);
      }
    });
  }

  // Helper method to get test status from the slider component's data
  private getTestStatus(taxonomyId: string, testId: string): string {
    if (!this.playgroundSlider) {
      return 'processed';
    }

    const taxonomies = this.playgroundSlider.taxonomies();
    const taxonomy = taxonomies.find(t => t.taxonomy_id === taxonomyId);

    if (!taxonomy) {
      return 'processed';
    }

    const test = taxonomy.test.find(t => t.test_id === testId);
    return test ? test.test_status : 'processed';
  }
}
